import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { journalAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import JournalEntryForm from './JournalEntryForm';
import JournalEntryList from './JournalEntryList';
import JournalStats from './JournalStats';
import { 
  PlusCircle, 
  List, 
  BarChart2
} from 'lucide-react';

const Journal = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  // Removed summary cards state
  // Templates removed per request
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => {
    if (user) {
      loadJournalData();
    }
  }, [user]);

  const loadJournalData = async () => {
    try {
      setLoading(true);
      const [summaryResponse] = await Promise.all([
        journalAPI.getSummary()
      ]);

      if (summaryResponse.data?.success) {
        setEntries(summaryResponse.data.data.recent_entries || []);
        setStats(summaryResponse.data.data.stats);
      } else if (summaryResponse.data) {
        // Handle case where API doesn't return success wrapper
        setEntries(summaryResponse.data.recent_entries || []);
        setStats(summaryResponse.data.stats);
      }

      // Removed summary counts computation

      // Templates removed
    } catch (error) {
      // Silence transient errors; UI already shows available data
      console.error('Error loading journal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = async (entryData) => {
    try {
      const response = await journalAPI.createEntry(entryData);
      const created = response.data?.data || response.data;
      setEntries(prev => [created, ...prev]);
      setShowForm(false);
      setEditingEntry(null);
      loadJournalData();
      toast.success('Journal entry created successfully!');
    } catch (error) {
      toast.error('Failed to create journal entry');
    }
  };

  const handleUpdateEntry = async (entryId, entryData) => {
    try {
      const response = await journalAPI.updateEntry(entryId, entryData);
      const updated = response.data?.data || response.data;
      setEntries(prev => prev.map(entry => entry.id === entryId ? updated : entry));
      setShowForm(false);
      setEditingEntry(null);
      loadJournalData();
      toast.success('Journal entry updated successfully!');
    } catch (error) {
      toast.error('Failed to update journal entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await journalAPI.deleteEntry(entryId);
      setEntries(prev => prev.filter(entry => entry.id !== entryId));
      loadJournalData(); // Refresh stats
      toast.success('Journal entry deleted successfully!');
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete journal entry');
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleNewEntry = () => {
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleEntrySaved = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  // Templates removed

  const handleRefresh = () => {
    loadJournalData();
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-900">
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Trading Journal</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track your trading journey, analyze your performance, and improve your strategies
        </p>
      </div>

      {/* Summary cards removed as requested */}

      {/* Navigation Tabs */}
      <div className="flex flex-wrap justify-start gap-4 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('entries')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg transition-colors duration-200 flex items-center gap-2 ${
            activeTab === 'entries'
              ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border-b-2 border-transparent'
          }`}
        >
          <List className="h-4 w-4" /> Entries
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg transition-colors duration-200 flex items-center gap-2 ${
            activeTab === 'stats'
              ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border-b-2 border-transparent'
          }`}
        >
          <BarChart2 className="h-4 w-4" /> Statistics
        </button>
        {/* Templates tab removed per request */}
      </div>

      {/* Tab Content */}
      {activeTab === 'entries' && (
        <div className="space-y-6">
          {/* New Entry Button */}
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Journal Entries</h2>
            <button
              onClick={handleNewEntry}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              New Entry
            </button>
          </div>

          {/* Entry Form */}
          {showForm && (
            <JournalEntryForm
              entry={editingEntry}
              onSubmit={(formData, existing) => existing ? handleUpdateEntry(existing.id, formData) : handleCreateEntry(formData)}
              onCancel={handleEntrySaved}
              onEntrySaved={handleEntrySaved}
            />
          )}

          {/* Entry List */}
          <JournalEntryList
            entries={entries}
            onEditEntry={handleEditEntry}
            onDelete={handleDeleteEntry}
            onRefresh={handleRefresh}
          />
        </div>
      )}

      {activeTab === 'stats' && (
        <JournalStats />
      )}

      {/* Background accent */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute right-[-10%] top-[-10%] h-72 w-72 rounded-full blur-3xl opacity-30 bg-emerald-300 dark:bg-emerald-600" />
        <div className="absolute left-[-10%] bottom-[-10%] h-72 w-72 rounded-full blur-3xl opacity-30 bg-indigo-300 dark:bg-indigo-600" />
      </div>
      </div>
    </div>
  );
};

export default Journal;